<template>
  <div class="open-api">
    <div class="bg-white rounded-md p-6 max-w-3xl mx-auto">
      <div v-if="isStatusLoading" class="text-sm text-neutral-500 py-8">
        Memuat status Open API...
      </div>
      <div v-else-if="isActiveApi === false" class="border border-neutral-200 rounded-lg p-5">
        <p class="text-base font-bold mb-2">Open API belum aktif</p>
        <p class="text-sm text-neutral-600 mb-4">
          Untuk mengaktifkan Open API, silakan hubungi admin melalui WhatsApp.
        </p>
        <a
          :href="waLink"
          target="_blank"
          rel="noopener"
          class="inline-flex items-center justify-center text-sm font-medium rounded border border-primary-400 text-primary-400 px-4 py-2 hover:border-primary-700 hover:text-primary-700 transition-all"
        >
          Hubungi Admin via WhatsApp
        </a>
      </div>
      <div v-else-if="isActiveApi === true">
        <div class="grid grid-cols-1 gap-6 mb-8">
          <div class="border border-neutral-200 rounded-lg p-4">
            <div class="flex mdmax:flex-col items-center mdmax:items-start justify-between mdmax:gap-3 mb-3">
              <p class="text-base font-bold text-neutral-900">API Key</p>
              <div class="flex gap-2 flex-wrap mdmax:w-full">
                <Button :buttonText="apiKeyVisible ? 'Hide' : 'Show'" type="secondary" size="small" @action="toggleApiKey()" class="mdmax:flex-1" />
                <Button buttonText="Copy" type="secondary" size="small" @action="copyApiKey()" class="mdmax:flex-1" />
                <Button buttonText="Regenerate" type="primary" size="small" @action="regenerateApiKey()" class="mdmax:w-full" />
              </div>
            </div>
            <TextField :value="apiKeyDisplay" readonly class="text-black" />
          </div>
          <div class="border border-neutral-200 rounded-lg p-4">
            <div class="flex mdmax:flex-col items-center mdmax:items-start justify-between mdmax:gap-3 mb-3">
              <p class="text-base font-bold text-neutral-900">Secret Key</p>
              <div class="flex gap-2 flex-wrap mdmax:w-full">
                <Button :buttonText="secretKeyVisible ? 'Hide' : 'Show'" type="secondary" size="small" @action="toggleSecretKey()" class="mdmax:flex-1" />
                <Button buttonText="Copy" type="secondary" size="small" @action="copySecretKey()" class="mdmax:flex-1" />
                <Button buttonText="Regenerate" type="primary" size="small" @action="regenerateSecretKey()" class="mdmax:w-full" />
              </div>
            </div>
            <TextField :value="secretKeyDisplay" readonly class="text-black" />
          </div>
        </div>
        <div class="border border-neutral-200 rounded-lg p-4 mb-6">
          <div class="flex mdmax:flex-col items-center mdmax:items-start justify-between mdmax:gap-3 mb-3">
            <p class="text-base font-bold">Webhook URL</p>
            <Button :buttonText="webhookEditable ? 'Cancel' : 'Edit'" type="secondary" size="small" @action="toggleWebhookEdit()" class="mdmax:w-full" />
          </div>
          <TextField v-model="webhookUrl" :disabled="!webhookEditable" placeholder="https://example.com/webhook" />
        </div>
        <a :href="docsUrl" target="_blank" rel="noopener" class="text-primary-400 text-sm font-medium hover:text-primary-700">
          Lihat dokumentasi Open API
        </a>

        <div class="flex justify-end mt-6">
          <Button buttonText="Update" type="primary" :disabled="updateLoading" :isLoading="updateLoading" @action="updateOpenApi()" />
        </div>
      </div>
      <div v-else class="border border-neutral-200 rounded-lg p-5">
        <p class="text-sm text-neutral-600 mb-4">
          Gagal memuat status Open API.
        </p>
        <Button buttonText="Coba lagi" type="primary" size="small" @action="fetchApiStatus()" />
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { mapActions } from 'vuex'

export default {
  components: {
    Button: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/Button'),
    TextField: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/TextField')
  },
  data: () => ({
    apiKey: 'api_1234567890',
    secretKey: 'secret_abcdef123456',
    apiKeyVisible: false,
    secretKeyVisible: false,
    webhookEditable: false,
    webhookUrl: 'https://example.com/webhook',
    docsUrl: 'https://linknau.id/en/open-api',
    isActiveApi: null,
    isStatusLoading: false,
    updateLoading: false,
    phoneNumber: '6285285890090',
    waMessage: 'Halo Admin, saya tertarik menggunakan Open API Linknau. Mohon info cara aktivasinya.'
  }),
  computed: {
    apiKeyDisplay() {
      return this.apiKeyVisible ? this.apiKey : '*****'
    },
    secretKeyDisplay() {
      return this.secretKeyVisible ? this.secretKey : '*****'
    },
    waLink() {
      return `https://wa.me/${this.phoneNumber}?text=${encodeURIComponent(this.waMessage)}`
    }
  },
  created() {
    this.fetchApiStatus()
  },
  methods: {
    ...mapActions('common', ['showLoading', 'hideLoading', 'setToast']),
    toggleApiKey() {
      this.apiKeyVisible = !this.apiKeyVisible
    },
    toggleSecretKey() {
      this.secretKeyVisible = !this.secretKeyVisible
    },
    async copyApiKey() {
      const copied = await this.copyToClipboard(this.apiKey)
      this.notifyCopyResult(copied, 'API Key')
    },
    async copySecretKey() {
      const copied = await this.copyToClipboard(this.secretKey)
      this.notifyCopyResult(copied, 'Secret Key')
    },
    regenerateApiKey() {
      this.apiKey = this.generateKey('api', 24)
    },
    regenerateSecretKey() {
      this.secretKey = this.generateKey('secret', 32)
    },
    toggleWebhookEdit() {
      this.webhookEditable = !this.webhookEditable
    },
    updateOpenApi() {
      if (this.updateLoading) return
      const payloads = {
        api_key: this.apiKey,
        secret_key: this.secretKey,
        webhook_url: this.webhookUrl
      }
      this.updateLoading = true
      this.showLoading()
      api.merchant
        .updateIntegrationApiKey(payloads)
        .then(() => {
          this.webhookEditable = false
          this.apiKeyVisible = false
          this.secretKeyVisible = false
          return this.fetchApiDetail({ silent: true })
        })
        .then(() => {
          this.setToast({
            options: {
              type: 'success',
              duration: 2000,
              primaryText: 'Open API berhasil diupdate',
              secondaryText: 'Perubahan telah disimpan',
              callback: () => {}
            }
          })
        })
        .catch(() => {
          this.setToast({
            options: {
              type: 'danger',
              duration: 2000,
              primaryText: 'Gagal update Open API',
              secondaryText: 'Silakan coba lagi',
              callback: () => {}
            }
          })
        })
        .finally(() => {
          this.hideLoading()
          this.updateLoading = false
        })
    },
    fetchApiStatus() {
      if (this.isStatusLoading) return
      this.isStatusLoading = true
      this.showLoading()
      api.merchant
        .getIntegrationApiKeyDetail()
        .then(response => {
          const detail = response?.data?.data || {}
          this.applyApiDetail(detail)
          return null
        })
        .catch(() => {
          this.isActiveApi = null
        })
        .finally(() => {
          this.hideLoading()
          this.isStatusLoading = false
        })
    },
    fetchApiDetail({ silent = false } = {}) {
      return api.merchant
        .getIntegrationApiKeyDetail()
        .then(response => {
          const detail = response?.data?.data || {}
          this.applyApiDetail(detail)
        })
        .catch(() => {
          if (!silent) {
            this.setToast({
              options: {
                type: 'danger',
                duration: 2000,
                primaryText: 'Gagal memuat detail Open API',
                secondaryText: 'Silakan coba lagi',
                callback: () => {}
              }
            })
          }
        })
    },
    applyApiDetail(detail = {}) {
      if (typeof detail.is_active_api === 'boolean') {
        this.isActiveApi = detail.is_active_api
      }
      if (detail.api_key) this.apiKey = detail.api_key
      if (detail.secret_key) this.secretKey = detail.secret_key
      if (typeof detail.webhook_url === 'string') this.webhookUrl = detail.webhook_url
      this.apiKeyVisible = false
      this.secretKeyVisible = false
      this.webhookEditable = false
    },
    generateKey(prefix, size) {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      let result = ''
      for (let index = 0; index < size; index += 1) {
        const randomIndex = Math.floor(Math.random() * chars.length)
        result += chars[randomIndex]
      }
      return `${prefix}_${result}`
    },
    notifyCopyResult(success, label) {
      this.setToast({
        options: {
          type: success ? 'success' : 'danger',
          duration: 2000,
          primaryText: success ? `${label} berhasil disalin` : `Gagal menyalin ${label}`,
          secondaryText: success ? 'Tersalin ke clipboard' : 'Silakan coba lagi',
          callback: () => {}
        }
      })
    },
    async copyToClipboard(value) {
      if (navigator?.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(value)
          return true
        } catch (error) {
          // fallback to legacy copy
        }
      }
      let textarea = null
      try {
        textarea = document.createElement('textarea')
        textarea.value = value
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'absolute'
        textarea.style.left = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()
        return document.execCommand('copy')
      } catch (error) {
        return false
      } finally {
        if (textarea?.parentNode) {
          document.body.removeChild(textarea)
        }
      }
    }
  }
}
</script>
